import React from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import TitleButtons from "../../../../../components/TitleButtons";
import HistoricoWidget, {
  HistoricoItem,
} from "../../../../../components/tableHistorico";
import HistoricoPrecosWidget from "../../../../../components/tablePrice";

// ====================== Tipos de API ======================
type ApiProduto = {
  id: number;
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;
  vendavel: boolean;
  responsavel_registro: number | null;
  departamento: string;
  criado_em: string;
  atualizado_em: string;
  dimensao?: string | null;
  peso?: string | null;
  ocultar?: boolean | null;
};

type ApiMovItem = HistoricoItem & {
  tipo_full?: string | null;
  custo_unitario?: number | string | null;
};

type MovimentosPorTipo = {
  entrada?: ApiMovItem[];
  saida?: ApiMovItem[];
  perda?: ApiMovItem[];
  venda?: ApiMovItem[];
  consumo?: ApiMovItem[];
  retorno?: ApiMovItem[];
  standby?: ApiMovItem[];
  [k: string]: ApiMovItem[] | undefined;
};

type ApiEstoqueAtual = {
  produto_id: number;
  quantidade_atual: number | null;
  custo_unitario_atual: string | number | null;
  moeda?: string | null;
};

type ApiResponse = {
  ok: boolean;
  oculto?: boolean;
  data?: {
    produto: ApiProduto;
    custos: ApiMovItem[];
    movimentos_por_tipo?: MovimentosPorTipo;
    estoque_atual: ApiEstoqueAtual | null;
  };
  erro?: string;
};

type AuthMe = {
  ok: boolean;
  sessao?: {
    uid?: number;
    email: string;
    nome: string;
    picture?: string;
    ts?: number;
    ttl?: number;
  };
};

// ====================== UI Auxiliar ======================
const NoticeBanner: React.FC<{
  type: "success" | "error" | "notify";
  children: React.ReactNode;
}> = ({ type, children }) => {
  let bg = "#e7f1ff",
    color = "#084298",
    border = "#b6d4fe";
  if (type === "success") {
    bg = "#e6ffed";
    color = "#0f5132";
    border = "#badbcc";
  }
  if (type === "error") {
    bg = "#ffecec";
    color = "#842029";
    border = "#f5c2c7";
  }
  return (
    <div
      role="alert"
      style={{
        margin: "12px 40px 0 40px",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border,
        borderStyle: "solid",
        borderWidth: 1,
      }}
    >
      {children}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div style={{ marginBottom: "24px" }}>
    <div
      style={{
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: "15px",
        marginBottom: "4px",
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: "Inter", fontWeight: 300, fontSize: "14px" }}>
      {value}
    </div>
  </div>
);

// ====================== Página ======================
const DetalhesProduto: React.FC = () => {
  const { codigo: codigoParam } = useParams<{ codigo: string }>();
  const codigo = React.useMemo(
    () => decodeURIComponent(codigoParam ?? ""),
    [codigoParam]
  );

  const navigate = useNavigate();

  // ---- States
  const [ocultando, setOcultando] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<ApiResponse["data"] | null>(
    null
  );
  const [isOculto, setIsOculto] = React.useState<boolean>(false);

  // Toggle entre tabelas: "mov" | "precos"
  const [activeTab, setActiveTab] = React.useState<"mov" | "precos">("mov");

  // Painel de Ajuste
  const [showAdjust, setShowAdjust] = React.useState(false);
  const [adjustMode, setAdjustMode] = React.useState<"quantidade" | "preco">(
    "quantidade"
  );
  const [qtyInput, setQtyInput] = React.useState<string>("");

  const [costDisplay, setCostDisplay] = React.useState<string>("");
  const [costValue, setCostValue] = React.useState<number | null>(null);
  const [selectedCurrency, setSelectedCurrency] = React.useState<
    "BRL" | "USD" | "EUR"
  >("BRL");

  const [adjustLoading, setAdjustLoading] = React.useState(false);
  const [adjustError, setAdjustError] = React.useState<string | null>(null);
  const [adjustSuccess, setAdjustSuccess] = React.useState<string | null>(null);

  // UID já autenticado
  const [authUid, setAuthUid] = React.useState<number | null>(null);

  // ---- formatadores
  const formatCurrency = (
    v: string | number | null | undefined,
    currency?: "BRL" | "USD" | "EUR" | null
  ) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) return "—";
    const cur = (currency || "BRL") as "BRL" | "USD" | "EUR";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: cur,
    }).format(n);
  };

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return "—";
    const m = iso.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
    );
    if (!m) return iso || "—";
    const [, ano, mes, dia, hora, minuto] = m;
    return `${dia}/${mes}/${ano} - ${hora}:${minuto}`;
  };

  const formatByCurrency = React.useCallback(
    (value: number, currency: "BRL" | "USD" | "EUR") => {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    []
  );

  const handleCostInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value || "";
    const onlyDigits = raw.replace(/\D/g, "");
    const cents = Number(onlyDigits);
    const val = Number.isFinite(cents) ? cents / 100 : 0;
    setCostValue(Number.isFinite(val) ? val : null);
    setCostDisplay(
      onlyDigits.length ? formatByCurrency(val, selectedCurrency) : ""
    );
  };

  const costKeyDownFilter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowed =
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "Tab" ||
      /^\d$/.test(e.key);
    if (!allowed) e.preventDefault();
  };

  React.useEffect(() => {
    if (costValue !== null) {
      setCostDisplay(formatByCurrency(costValue, selectedCurrency));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency]);

  // ---- /auth/me
  React.useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const res = await fetch("https://api.eva-googlemarine.com/auth/me", {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error(`Falha em /auth/me (HTTP ${res.status})`);
        const json: AuthMe = await res.json();
        const rawUid = json?.sessao?.uid;
        const nextUid: number | null =
          typeof rawUid === "number" && Number.isInteger(rawUid)
            ? rawUid
            : null;

        if (!disposed) {
          setAuthUid(nextUid);
          if (nextUid !== null) {
            try {
              (window as any).__EVA_AUTH_UID__ = nextUid;
              localStorage.setItem("eva_auth_uid", String(nextUid));
            } catch {}
          }
        }
      } catch {
        if (!disposed) setAuthUid(null);
      }
    })();
    return () => {
      disposed = true;
    };
  }, []);

  // ---- (re)carregar o produto ----
  const fetchProduto = async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `https://api.eva-googlemarine.com/departamentos/tecnicos/produtos/${encodeURIComponent(
          codigo
        )}`,
        { signal }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: ApiResponse = await res.json();
      if (!json.ok || !json.data)
        throw new Error(json.erro || "Resposta inválida da API");
      setPayload(json.data);
      setIsOculto(Boolean(json.oculto) || Boolean(json.data.produto?.ocultar));
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setError(e?.message || "Falha ao buscar produto");
        setPayload(null);
        setIsOculto(false);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!codigo) return;
    const abort = new AbortController();
    fetchProduto(abort.signal);
    return () => abort.abort();
  }, [codigo]);

  // ========= Histórico completo (SEM ajuste-preco) =========
  const movimentosOrdenados: ApiMovItem[] = React.useMemo(() => {
    const mov = payload?.movimentos_por_tipo;
    if (!mov) return [];

    const all: ApiMovItem[] = (
      Object.values(mov).flatMap((arr) => arr ?? []) as ApiMovItem[]
    ).filter((i) => {
      const t = String((i as any).tipo_full || i.tipo || "").toLowerCase();
      return !(t === "ajuste-preco" || t.includes("ajuste-preco"));
    });

    const withDisplayTipo: ApiMovItem[] = all.map((i) => {
      const tipoRaw = String(
        (i as any).tipo_full || i.tipo || ""
      ).toLowerCase();
      const isAjusteQuantidade =
        tipoRaw === "ajuste" ||
        (tipoRaw.includes("ajuste") && !tipoRaw.includes("preco"));
      return isAjusteQuantidade ? { ...i, tipo: "Ajuste" } : i;
    });

    return withDisplayTipo.sort((a, b) => {
      const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
      const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;
      return tb - ta;
    });
  }, [payload?.movimentos_por_tipo]);

  // ========= Histórico de Preços (custos + ajustes de preço) =========
  const precoHistoricoItems: ApiMovItem[] = React.useMemo(() => {
    const custos = payload?.custos ?? [];
    const base: ApiMovItem[] = Array.isArray(custos) ? custos : [];

    const mov = payload?.movimentos_por_tipo;
    let ajustesPreco: ApiMovItem[] = [];
    if (mov) {
      const all: ApiMovItem[] = Object.values(mov).flatMap(
        (arr) => arr ?? []
      ) as ApiMovItem[];
      ajustesPreco = all
        .filter((i) => {
          const t = String((i as any).tipo_full || i.tipo || "").toLowerCase();
          return t === "ajuste-preco" || t.includes("ajuste-preco");
        })
        .map((i) => {
          const cuRaw =
            (i as any).custo_unitario ??
            (i as any).custo_unit ??
            (i as any).preco ??
            null;
          const cu =
            typeof cuRaw === "string"
              ? Number(cuRaw)
              : (cuRaw as number | null);

          const moeda =
            (i as any).moeda ||
            (i as any).item_moeda ||
            (payload?.estoque_atual?.moeda ?? null);

          return {
            ...i,
            tipo: "Ajuste de Preço",
            custo_unitario: Number.isFinite(cu as number)
              ? (cu as number)
              : null,
            // @ts-ignore
            moeda: (typeof moeda === "string"
              ? moeda.toUpperCase()
              : null) as any,
          };
        });
    }

    const merged = [...base, ...ajustesPreco];

    return merged.sort((a, b) => {
      const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
      const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;
      return tb - ta;
    });
  }, [
    payload?.custos,
    payload?.movimentos_por_tipo,
    payload?.estoque_atual?.moeda,
  ]);

  const moedaAtual: "BRL" | "USD" | "EUR" | null = React.useMemo(() => {
    const est = payload?.estoque_atual?.moeda;
    if (est === "BRL" || est === "USD" || est === "EUR") return est;
    const mHist = (precoHistoricoItems?.[0] as any)?.moeda;
    return mHist === "USD" || mHist === "EUR" ? mHist : "BRL";
  }, [payload?.estoque_atual?.moeda, precoHistoricoItems]);

  const custoUnitario = React.useMemo(() => {
    const atual = payload?.estoque_atual?.custo_unitario_atual;
    const hist = precoHistoricoItems?.[0]?.custo_unitario as
      | number
      | string
      | null
      | undefined;
    const val =
      (typeof atual === "number" ? atual : Number(atual)) ??
      (typeof hist === "number" ? hist : Number(hist)) ??
      null;
    return formatCurrency(val, moedaAtual);
  }, [payload, precoHistoricoItems, moedaAtual]);

  const quantidadeEstoque = React.useMemo(() => {
    const q = payload?.estoque_atual?.quantidade_atual;
    return q == null ? "—" : String(q);
  }, [payload]);

  if (!codigo) return <Navigate to="/suprimentos/produtos" replace />;

  // ---- ações

  // (mantive a função handleOcultar se quiser reaproveitar depois,
  // mas o botão agora abre a tela de edição)
  const handleOcultar = async () => {
    if (!codigo || ocultando) return;
    const confirma = window.confirm(`Ocultar o produto ${codigo}?`);
    if (!confirma) return;
    try {
      setOcultando(true);
      const r1 = await fetch(
        "https://api.eva-googlemarine.com/ocultar-produto",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ codigo }),
        }
      );
      const t1 = await r1.text().catch(() => "");
      let j1: any = null;
      try {
        j1 = t1 ? JSON.parse(t1) : null;
      } catch {}
      if (!r1.ok || j1?.ok === false)
        throw new Error(j1?.erro || `Falha ao ocultar (HTTP ${r1.status})`);

      const r2 = await fetch(
        "https://api.eva-googlemarine.com/departamentos/suprimentos/produtos/invalidate",
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      );
      if (!r2.ok) {
        const t2 = await r2.text().catch(() => "");
        throw new Error(
          `Falha ao invalidar cache (HTTP ${r2.status}) ${t2 || ""}`.trim()
        );
      }
      navigate("/suprimentos/produtos", { replace: true });
    } catch (e: any) {
      window.alert(e?.message || "Falha ao ocultar produto.");
    } finally {
      setOcultando(false);
    }
  };

  const handleOpenAdjust = () => {
    setAdjustError(null);
    setAdjustSuccess(null);
    setQtyInput("");
    setAdjustMode("quantidade");

    const atualNum =
      typeof payload?.estoque_atual?.custo_unitario_atual === "number"
        ? (payload?.estoque_atual?.custo_unitario_atual as number)
        : Number(payload?.estoque_atual?.custo_unitario_atual ?? 0);
    if (Number.isFinite(atualNum) && atualNum >= 0) {
      setCostValue(atualNum);
      const estoqueMoeda = (payload?.estoque_atual?.moeda || "BRL") as
        | "BRL"
        | "USD"
        | "EUR";
      setSelectedCurrency(
        estoqueMoeda === "USD" || estoqueMoeda === "EUR" ? estoqueMoeda : "BRL"
      );
      setCostDisplay(formatByCurrency(atualNum, estoqueMoeda || "BRL"));
    } else {
      setCostValue(null);
      setCostDisplay("");
      setSelectedCurrency("BRL");
    }

    setShowAdjust(true);
  };

  const handleCancelAdjust = () => {
    if (adjustLoading) return;
    setShowAdjust(false);
    setAdjustError(null);
    setAdjustSuccess(null);
  };

  const handleConfirmAdjust = async () => {
    if (!codigo || adjustLoading) return;
    setAdjustError(null);
    setAdjustSuccess(null);

    if (!Number.isInteger(authUid as any)) {
      setAdjustError(
        "Sessão não disponível. Recarregue a página ou faça login novamente."
      );
      return;
    }

    try {
      setAdjustLoading(true);

      let body: any = {
        codigo,
        responsavel_id: authUid,
      };

      if (adjustMode === "quantidade") {
        const qNum = Number(qtyInput);
        if (!Number.isFinite(qNum) || !Number.isInteger(qNum)) {
          setAdjustLoading(false);
          setAdjustError("Digite um número inteiro válido para a quantidade.");
          return;
        }
        body.quantidade = qNum;
      } else {
        const quantidadeAtual = payload?.estoque_atual?.quantidade_atual ?? 0;
        if (costValue === null || !(costValue >= 0)) {
          setAdjustLoading(false);
          setAdjustError("Informe um custo unitário válido (ex.: 13503,45).");
          return;
        }
        body.custo_unitario = costValue;
        body.quantidade = quantidadeAtual;
        body.moeda = selectedCurrency;
      }

      const postRes = await fetch(
        "https://api.eva-googlemarine.com/ajustar-produto",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const postTxt = await postRes.text().catch(() => "");
      let postJson: any = null;
      try {
        postJson = postTxt ? JSON.parse(postTxt) : null;
      } catch {}

      if (!postRes.ok || postJson?.ok === false) {
        const msg =
          postJson?.erro ||
          `Falha ao ajustar (HTTP ${postRes.status}) ${postTxt || ""}`.trim();
        throw new Error(msg);
      }

      setAdjustSuccess(
        adjustMode === "quantidade"
          ? "Quantidade ajustada com sucesso."
          : "Preço ajustado com sucesso."
      );
      await fetchProduto();
      setShowAdjust(false);
    } catch (e: any) {
      setAdjustError(
        e?.message ||
          (adjustMode === "quantidade"
            ? "Falha ao ajustar quantidade."
            : "Falha ao ajustar preço.")
      );
    } finally {
      setAdjustLoading(false);
    }
  };

  // ===== Derivados simples (sem hooks)
  const nome = payload?.produto?.nome ?? "—";
  const ncm = payload?.produto?.ncm ?? "—";
  const classificacao = payload?.produto?.classificacao ?? "—";
  const posicao = payload?.produto?.posicao ?? "—";
  const dimensao = payload?.produto?.dimensao ?? "—";
  const peso = payload?.produto?.peso ? `${payload.produto.peso}` : "—";

  // ====================== Estilos ======================
  const toggleWrapStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    padding: "0 40px",
    margin: "8px 0 12px 0",
    alignItems: "center",
  };
  const baseBtn: React.CSSProperties = {
    padding: "8px 14px",
    borderRadius: 4,
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #ccc",
    background: "#124280",
    color: "#fff",
    outline: "none",
    userSelect: "none",
    transition:
      "transform 0.02s ease-in-out, box-shadow 0.1s ease-in-out, opacity 0.12s",
  };
  const btnActive: React.CSSProperties = {
    ...baseBtn,
    boxShadow: "0 0 0 1px rgba(18,66,128,0.15)",
    opacity: 1,
  };
  const btnInactive: React.CSSProperties = { ...baseBtn, opacity: 0.7 };
  const adjustPanelWrap: React.CSSProperties = {
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: "0 40px",
    marginTop: 8,
    flexWrap: "wrap",
  };
  const adjustInputStyle: React.CSSProperties = {
    width: 180,
    height: 34,
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "0 10px",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
  };
  const selectStyle: React.CSSProperties = {
    width: 120,
    height: 34,
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "0 10px",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    background: "#fff",
  };
  const adjustBtnConfirm: React.CSSProperties = {
    ...baseBtn,
    background: "#114280",
    borderColor: "#114280",
  };
  const adjustBtnCancel: React.CSSProperties = {
    ...baseBtn,
    background: "#6c757d",
    borderColor: "#6c757d",
  };

  return (
    <div>
      <TitleButtons
        title={`Suprimentos - Detalhes do Produto / ${codigo}`}
        buttonMarginRight="40px"
        buttons={[
          {
            label: "Alterar dados do Produto",
            onClick: () =>
              navigate(
                `/suprimentos/produtos/${encodeURIComponent(codigo)}/editar`
              ),
            variant: "primary",
          },
          {
            label: "Ajustar Quantidade/Preço",
            onClick: handleOpenAdjust,
            variant: "primary",
          },
        ]}
      />

      {isOculto && (
        <NoticeBanner type="notify">
          Este produto está marcado como <strong>oculto</strong>. Ele não
          aparecerá nas listagens padrão.
        </NoticeBanner>
      )}

      {/* Painel de Ajuste */}
      {showAdjust && (
        <div style={adjustPanelWrap}>
          <fieldset
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            <legend
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                opacity: 0.8,
              }}
            >
              Modo
            </legend>
            <label
              style={{
                marginRight: 12,
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
              }}
            >
              <input
                type="radio"
                name="ajuste-modo"
                value="quantidade"
                checked={adjustMode === "quantidade"}
                onChange={() => setAdjustMode("quantidade")}
                disabled={adjustLoading}
              />{" "}
              Ajustar Quantidade
            </label>
            <label style={{ fontFamily: "Inter, sans-serif", fontSize: 13 }}>
              <input
                type="radio"
                name="ajuste-modo"
                value="preco"
                checked={adjustMode === "preco"}
                onChange={() => setAdjustMode("preco")}
                disabled={adjustLoading}
              />{" "}
              Ajustar Preço (não altera quantidade)
            </label>
          </fieldset>

          {adjustMode === "quantidade" ? (
            <label style={{ fontFamily: "Inter, sans-serif", fontSize: 13 }}>
              Quantidade:&nbsp;
              <input
                type="number"
                step="1"
                inputMode="numeric"
                style={adjustInputStyle}
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
                placeholder="Ex.: 42"
                disabled={adjustLoading}
              />
            </label>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <label style={{ fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                Custo Unitário:&nbsp;
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  style={adjustInputStyle}
                  value={costDisplay}
                  onChange={handleCostInputChange}
                  onKeyDown={costKeyDownFilter}
                  placeholder={formatByCurrency(0, selectedCurrency).replace(
                    /\d+([.,]\d+)?/,
                    "0,00"
                  )}
                  disabled={adjustLoading}
                />
              </label>

              <label style={{ fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                Moeda:&nbsp;
                <select
                  value={selectedCurrency}
                  onChange={(e) =>
                    setSelectedCurrency(e.target.value as "BRL" | "USD" | "EUR")
                  }
                  style={selectStyle}
                  disabled={adjustLoading}
                >
                  <option value="BRL">BRL</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </label>

              <div
                title="Quantidade atual que será usada no item de ajuste de preço"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 12,
                  opacity: 0.8,
                }}
              >
                Qtde atual:{" "}
                <strong>{payload?.estoque_atual?.quantidade_atual ?? 0}</strong>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleConfirmAdjust}
            style={adjustBtnConfirm}
            disabled={adjustLoading}
            aria-busy={adjustLoading}
          >
            {adjustLoading ? "Aplicando..." : "Confirmar"}
          </button>
          <button
            type="button"
            onClick={handleCancelAdjust}
            style={adjustBtnCancel}
            disabled={adjustLoading}
          >
            Cancelar
          </button>
          {adjustError && (
            <span style={{ color: "#b02a37", marginLeft: 8, fontSize: 13 }}>
              {adjustError}
            </span>
          )}
          {adjustSuccess && (
            <span style={{ color: "#0f5132", marginLeft: 8, fontSize: 13 }}>
              {adjustSuccess}
            </span>
          )}
        </div>
      )}

      {/* bloco 2 colunas */}
      <div
        style={{
          display: "flex",
          padding: "40px",
          gap: "100px",
          marginLeft: "0px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          <Field
            label="Nome do Produto"
            value={loading ? "Carregando..." : error ? "—" : nome}
          />
          <Field label="Código do Produto" value={codigo} />
          <Field
            label="Código NCM do Produto"
            value={loading ? "Carregando..." : error ? "—" : ncm}
          />
          <Field
            label="Classificação"
            value={loading ? "Carregando..." : error ? "—" : classificacao}
          />
          <Field
            label="Dimensão"
            value={loading ? "Carregando..." : error ? "—" : dimensao}
          />
          <Field
            label="Peso"
            value={loading ? "Carregando..." : error ? "—" : peso}
          />
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <Field
            label="Posição do Produto"
            value={loading ? "Carregando..." : error ? "—" : posicao}
          />
          <Field
            label="Custo Unitário (Atual / Último)"
            value={loading ? "Carregando..." : error ? "—" : custoUnitario}
          />
          <Field
            label="Quantidade no Estoque"
            value={loading ? "Carregando..." : error ? "—" : quantidadeEstoque}
          />
          {error && (
            <div
              style={{
                marginTop: "24px",
                color: "#c0362c",
                fontFamily: "Inter",
                fontSize: 13,
              }}
            >
              Erro ao carregar dados do produto: {error}
            </div>
          )}
        </div>
      </div>

      {/* Toggle entre tabelas */}
      <div style={toggleWrapStyle} role="group" aria-label="Alternar tabela">
        <button
          type="button"
          onClick={() => setActiveTab("mov")}
          style={activeTab === "mov" ? btnActive : btnInactive}
          aria-pressed={activeTab === "mov"}
        >
          Histórico de Movimentações
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("precos")}
          style={activeTab === "precos" ? btnActive : btnInactive}
          aria-pressed={activeTab === "precos"}
        >
          Histórico de Preços
        </button>
      </div>

      {/* Tabelas */}
      <div style={{ padding: "0 40px", marginLeft: "0px", marginTop: "4px" }}>
        {activeTab === "mov" ? (
          loading ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
              Carregando histórico…
            </div>
          ) : movimentosOrdenados.length === 0 ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
              Sem movimentações para este produto.
            </div>
          ) : (
            <HistoricoWidget
              items={movimentosOrdenados}
              loading={loading}
              error={error}
              formatCurrency={(v, m) => formatCurrency(v, (m as any) || "BRL")}
              formatDateTime={formatDateTime}
            />
          )
        ) : (
          <HistoricoPrecosWidget
            items={precoHistoricoItems}
            loading={loading}
            error={error}
            formatCurrency={(v) => formatCurrency(v, "BRL")}
            formatDateTime={formatDateTime}
          />
        )}
      </div>
    </div>
  );
};

export default DetalhesProduto;
